import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { MaterialModule } from '../../../shared/material.module';
import { UserType, PlanType, AgencyRegistrationRequest, CorporateRegistrationRequest } from '../../../types/auth.types';
import { OrganizationService, DuplicateCheckRequest } from '../../../services/organization.service';

export interface RegistrationData {
  personalInfo: {
    displayName: string;
    email: string;
    mobileNumber: string;
    accountType: UserType;
  };
  organizationInfo?: {
    name: string;
    panNumber?: string;
    aadharNumber?: string; // For agency
    gstNumber?: string; // For corporate
    cin?: string; // For corporate (Corporate Identification Number)
    address?: string;
    city?: string;
    state?: string;
    pincode?: string;
    companyType?: string; // For corporate
  };
}

@Component({
  selector: 'app-multi-step-registration',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MaterialModule],
  templateUrl: './multi-step-registration.component.html',
  styleUrls: ['./multi-step-registration.component.scss']
})
export class MultiStepRegistrationComponent implements OnInit {
  @Input() firebaseUser: any = null;
  @Input() isLoading = false;
  @Output() registrationSubmit = new EventEmitter<RegistrationData>();
  @Output() cancel = new EventEmitter<void>();

  accountTypeForm: FormGroup;
  personalInfoForm: FormGroup;
  organizationForm: FormGroup;
  isDuplicateChecking = false;
  duplicateCheckResult: { isDuplicate: boolean; conflictFields: string[]; message?: string } | null = null;

  accountTypes = [
    {
      value: 'individual',
      label: 'Individual',
      icon: 'person',
      description: 'Personal digital business card'
    },
    {
      value: 'agency',
      label: 'Agency',
      icon: 'groups',
      description: 'For agencies and small businesses'
    },
    {
      value: 'corporate',
      label: 'Corporate',
      icon: 'business',
      description: 'For larger organizations'
    }
  ];

  companyTypes = [
    { value: 'private_limited', label: 'Private Limited Company' },
    { value: 'public_limited', label: 'Public Limited Company' },
    { value: 'llp', label: 'Limited Liability Partnership (LLP)' },
    { value: 'partnership', label: 'Partnership Firm' },
    { value: 'sole_proprietorship', label: 'Sole Proprietorship' }
  ];

  constructor(
    private fb: FormBuilder,
    private organizationService: OrganizationService
  ) {
    this.accountTypeForm = this.fb.group({
      accountType: ['', Validators.required]
    });

    this.personalInfoForm = this.fb.group({
      displayName: ['', [Validators.required, Validators.minLength(2)]],
      email: ['', [Validators.required, Validators.email]],
      mobileNumber: ['', [Validators.required]]
    });

    this.organizationForm = this.fb.group({
      name: [''],
      panNumber: [''],
      aadharNumber: [''], // For agency personal details
      gstNumber: [''], // For corporate
      cin: [''], // For corporate (Corporate Identification Number)
      address: [''],
      city: [''],
      state: [''],
      pincode: [''],
      companyType: [''] // For corporate
    });
  }

  ngOnInit(): void {
    this.prefillFirebaseData();
    this.setupFormValidations();
  }

  private prefillFirebaseData(): void {
    if (this.firebaseUser) {
      if (this.firebaseUser.displayName) {
        this.personalInfoForm.patchValue({
          displayName: this.firebaseUser.displayName
        });
      }
      if (this.firebaseUser.email) {
        this.personalInfoForm.patchValue({
          email: this.firebaseUser.email
        });
      }
      if (this.firebaseUser.phoneNumber) {
        this.personalInfoForm.patchValue({
          mobileNumber: this.firebaseUser.phoneNumber
        });
      }
    }
  }

  private setupFormValidations(): void {
    // Watch for account type changes to update organization form validators
    this.accountTypeForm.get('accountType')?.valueChanges.subscribe((accountType: UserType) => {
      if (accountType !== 'individual') {
        this.updateOrganizationFormValidators(accountType);
      }
    });
  }

  private updateOrganizationFormValidators(accountType: UserType): void {
    // Reset all validators
    Object.keys(this.organizationForm.controls).forEach(key => {
      this.organizationForm.get(key)?.clearValidators();
    });

    // Set basic required validators
    this.organizationForm.get('name')?.setValidators([Validators.required, Validators.minLength(2)]);
    this.organizationForm.get('address')?.setValidators([Validators.required]);
    this.organizationForm.get('city')?.setValidators([Validators.required]);
    this.organizationForm.get('state')?.setValidators([Validators.required]);
    this.organizationForm.get('pincode')?.setValidators([Validators.required]);

    if (accountType === 'agency') {
      // Agency-specific required fields
      this.organizationForm.get('panNumber')?.setValidators([Validators.required]);
      this.organizationForm.get('aadharNumber')?.setValidators([Validators.required]);
    } else if (accountType === 'corporate') {
      // Corporate-specific required fields
      this.organizationForm.get('panNumber')?.setValidators([Validators.required]);
      this.organizationForm.get('companyType')?.setValidators([Validators.required]);
      this.organizationForm.get('cin')?.setValidators([Validators.required]);
      this.organizationForm.get('gstNumber')?.setValidators([Validators.required]);
    }

    // Update form validity
    this.organizationForm.updateValueAndValidity();
  }

  onSubmit(): void {
    const accountType = this.accountTypeForm.get('accountType')?.value;
    const personalInfo = this.personalInfoForm.value;

    let registrationData: RegistrationData = {
      personalInfo: {
        ...personalInfo,
        accountType: accountType  // Add account type from step 1
      }
    };

    if (accountType !== 'individual') {
      if (this.organizationForm.valid) {
        registrationData.organizationInfo = this.organizationForm.value;
      } else {
        this.markFormGroupTouched(this.organizationForm);
        return;
      }
    }

    this.registrationSubmit.emit(registrationData);
  }

  onCancel(): void {
    this.cancel.emit();
  }

  onMobileNumberInput(event: any): void {
  }

  checkForDuplicates(): boolean {
    const panNumber = this.organizationForm.get('panNumber')?.value;
    const gstNumber = this.organizationForm.get('gstNumber')?.value;
    const aadharNumber = this.organizationForm.get('aadharNumber')?.value;

    if (!panNumber && !gstNumber && !aadharNumber) {
      return false;
    }

    // Here you would typically make an API call to check for duplicates
    // For now, returning false as a placeholder
    return false;
  }

  getErrorMessage(fieldName: string, form: FormGroup): string {
    const field = form.get(fieldName);
    if (field?.hasError('required')) {
      return `${this.getFieldDisplayName(fieldName)} is required`;
    }
    if (field?.hasError('email')) {
      return 'Please enter a valid email address';
    }
    if (field?.hasError('minlength')) {
      const requiredLength = field.errors?.['minlength']?.requiredLength;
      return `${this.getFieldDisplayName(fieldName)} must be at least ${requiredLength} characters`;
    }
    if (field?.hasError('pattern')) {
      return `Please enter a valid ${this.getFieldDisplayName(fieldName)}`;
    }
    return '';
  }

  private getFieldDisplayName(fieldName: string): string {
    const displayNames: { [key: string]: string } = {
      displayName: 'Full Name',
      email: 'Email Address',
      mobileNumber: 'Mobile Number',
      accountType: 'Account Type',
      name: 'Organization Name',
      panNumber: 'PAN Number',
      aadharNumber: 'Aadhar Number',
      gstNumber: 'GST Number',
      cin: 'Corporate Identification Number (CIN)',
      address: 'Address',
      city: 'City',
      state: 'State',
      pincode: 'Pincode',
      companyType: 'Company Type'
    };
    return displayNames[fieldName] || fieldName;
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsTouched();
      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }
}
